// traceability: requirements: [FR-001, FR-002, FR-004, FR-006, FR-008]
import { lazy, Suspense } from 'react';
import { createBrowserRouter } from 'react-router-dom';
import { App } from './App';
import { Spinner } from '@/components/ui/Spinner';
import { ProtectedRoute } from '@/features/auth/ProtectedRoute';

function PageLoader() {
  return (
    <div className="flex min-h-[60vh] items-center justify-center">
      <Spinner size="lg" />
    </div>
  );
}

function lazyPage(importFn: () => Promise<{ default: React.ComponentType }>) {
  const LazyComponent = lazy(importFn);
  return function LazyRoutePage() {
    return (
      <Suspense fallback={<PageLoader />}>
        <LazyComponent />
      </Suspense>
    );
  };
}

const HomePage = lazyPage(() => import('@/features/home/HomePage'));
const LoginPage = lazyPage(() => import('@/features/auth/LoginPage'));
const RegisterPage = lazyPage(() => import('@/features/auth/RegisterPage'));
const OAuthCallbackPage = lazyPage(() => import('@/features/auth/OAuthCallbackPage'));
const MFASetup = lazyPage(() => import('@/features/auth/MFASetup'));
const QuestionnaireList = lazyPage(() => import('@/features/questionnaire/QuestionnaireList'));
const QuestionnairePage = lazyPage(() => import('@/features/questionnaire/QuestionnairePage'));
const PrivacyPolicyPage = lazyPage(() =>
  import('@/features/legal/LegalPages').then((m) => ({ default: m.PrivacyPolicyPage })),
);
const TermsOfServicePage = lazyPage(() =>
  import('@/features/legal/LegalPages').then((m) => ({ default: m.TermsOfServicePage })),
);
const ContactUsPage = lazyPage(() =>
  import('@/features/legal/LegalPages').then((m) => ({ default: m.ContactUsPage })),
);
const AnalysisPage = lazyPage(() => import('@/features/analysis/AnalysisPage'));
const AnalysisHistory = lazyPage(() => import('@/features/analysis/AnalysisHistory'));
const ShareLinkPage = lazyPage(() => import('@/features/analysis/ShareLinkPage'));
const CheckoutPage = lazyPage(() => import('@/features/payment/CheckoutPage'));
const PricingTiers = lazyPage(() => import('@/features/payment/PricingTiers'));
const PaymentSuccess = lazyPage(() => import('@/features/payment/PaymentSuccess'));
const BookingPage = lazyPage(() => import('@/features/booking/BookingPage'));
const MyBookings = lazyPage(() => import('@/features/booking/MyBookings'));
const ProfilePage = lazyPage(() => import('@/features/profile/ProfilePage'));
const AccountSettings = lazyPage(() => import('@/features/profile/AccountSettings'));

const AdminLayout = lazyPage(() => import('@/features/admin/AdminLayout'));
const DashboardPage = lazyPage(() => import('@/features/admin/DashboardPage'));
const QuestionnaireManager = lazyPage(() => import('@/features/admin/QuestionnaireManager'));
const QuestionEditor = lazyPage(() => import('@/features/admin/QuestionEditor'));
const ScoringRuleEditor = lazyPage(() => import('@/features/admin/ScoringRuleEditor'));
const KeywordManager = lazyPage(() => import('@/features/admin/KeywordManager'));
const TranslationManager = lazyPage(() => import('@/features/admin/TranslationManager'));
const PricingManager = lazyPage(() => import('@/features/admin/PricingManager'));
const BookingManager = lazyPage(() => import('@/features/admin/BookingManager'));
const TreatmentManager = lazyPage(() => import('@/features/admin/TreatmentManager'));
const MyCalendar = lazyPage(() => import('@/features/admin/MyCalendar'));
const UserManager = lazyPage(() => import('@/features/admin/UserManager'));
const AdminAssessment = lazyPage(() => import('@/features/admin/AdminAssessment'));
const IntakeManager = lazyPage(() => import('@/features/intake/IntakeManager'));
const IntakeReport = lazyPage(() => import('@/features/intake/IntakeReport'));
const BackupManager = lazyPage(() => import('@/features/admin/BackupManager'));
const AuditLog = lazyPage(() => import('@/features/admin/AuditLog'));
const NewsletterManager = lazyPage(() => import('@/features/admin/NewsletterManager'));
const StatisticsPage = lazyPage(() => import('@/features/admin/StatisticsPage'));

export const router = createBrowserRouter([
  {
    path: '/',
    element: <App />,
    children: [
      { index: true, element: <HomePage /> },
      { path: 'login', element: <LoginPage /> },
      { path: 'register', element: <RegisterPage /> },
      { path: 'auth/oauth/callback', element: <OAuthCallbackPage /> },
      { path: 'questionnaire', element: <QuestionnaireList /> },
      { path: 'questionnaire/:id', element: <QuestionnairePage /> },
      { path: 'privacy', element: <PrivacyPolicyPage /> },
      { path: 'terms', element: <TermsOfServicePage /> },
      { path: 'contact', element: <ContactUsPage /> },
      { path: 'analysis/link/:token', element: <ShareLinkPage /> },
      { path: 'pricing', element: <PricingTiers /> },
      {
        element: <ProtectedRoute />,
        children: [
          { path: 'analysis', element: <AnalysisHistory /> },
          { path: 'analysis/:id', element: <AnalysisPage /> },
          { path: 'checkout/:tierId', element: <CheckoutPage /> },
          { path: 'payment/success', element: <PaymentSuccess /> },
          { path: 'booking', element: <BookingPage /> },
          { path: 'my-bookings', element: <MyBookings /> },
          { path: 'profile', element: <ProfilePage /> },
          { path: 'profile/settings', element: <AccountSettings /> },
          { path: 'profile/mfa', element: <MFASetup /> },
        ],
      },
      {
        path: 'admin',
        element: <ProtectedRoute requiredRole="admin" />,
        children: [
          {
            element: <AdminLayout />,
            children: [
              { index: true, element: <DashboardPage /> },
              { path: 'users', element: <UserManager /> },
              { path: 'questionnaires', element: <QuestionnaireManager /> },
              { path: 'questions', element: <QuestionEditor /> },
              { path: 'scoring', element: <ScoringRuleEditor /> },
              { path: 'keywords', element: <KeywordManager /> },
              { path: 'translations', element: <TranslationManager /> },
              { path: 'pricing', element: <PricingManager /> },
              { path: 'bookings', element: <BookingManager /> },
              { path: 'my-calendar', element: <MyCalendar /> },
              { path: 'treatments', element: <TreatmentManager /> },
              { path: 'assessment', element: <AdminAssessment /> },
              { path: 'intakes', element: <IntakeManager /> },
              { path: 'intakes/report/:intakeId', element: <IntakeReport /> },
              { path: 'statistics', element: <StatisticsPage /> },
              { path: 'backups', element: <BackupManager /> },
              { path: 'audit', element: <AuditLog /> },
              { path: 'newsletter', element: <NewsletterManager /> },
            ],
          },
        ],
      },
    ],
  },
]);
